/**
 * Recently Viewed Products
 * ------------------------
 * Tracks recently viewed products in localStorage and renders them into the
 * <recently-viewed-products> section on the product page.
 *
 * How storage works
 * ------------------
 * A single localStorage key holds a JSON array of product handles, ordered
 * most-recent-first:
 *
 *   localStorage["recently-viewed-products"] = '["blue-shirt","red-hat", ...]'
 *
 * On each product page load we prepend the current handle, dedupe (keeping the
 * newest occurrence), and cap the list length. Handles — not full product
 * objects — are stored so the payload stays tiny and product data (price,
 * availability, images) is always fetched fresh at render time.
 *
 * Rendering
 * ---------
 * Cards are NOT rebuilt in JavaScript. Instead each handle is fetched through
 * Shopify's Section Rendering API, which returns the theme's real
 * card-product markup:
 *
 *   /products/{handle}?section_id=recently-viewed-card
 *
 * Fragments are fetched in parallel, filtered for failures (deleted /
 * unavailable products are skipped), then injected into the DOM in one pass.
 * The layout (slider-component, grid, dots) mirrors the Related / Upsell
 * metafield sections; once cards are injected we upgrade the grid to a slider
 * when needed and hand off to kingsley-featured-collection.js via the theme's
 * `shopify:section:load` hook.
 */

(() => {
  const STORAGE_KEY = 'recently-viewed-products';
  const HARD_LIMIT = 50;

  /**
   * Safely read the stored handle list. Returns [] on any parse/access error
   * (e.g. Safari private mode, corrupted value) so the feature never throws.
   * @returns {string[]}
   */
  function readHandles() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Persist the handle list, swallowing quota/access errors.
   * @param {string[]} handles
   */
  function writeHandles(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
    }
  }

  /**
   * Record the current product as most-recently-viewed: prepend, dedupe
   * (newest wins), and cap to HARD_LIMIT.
   * @param {string} handle
   * @returns {string[]} the updated list
   */
  function trackCurrentProduct(handle) {
    if (!handle) return readHandles();
    const existing = readHandles().filter((h) => h !== handle);
    const updated = [handle, ...existing].slice(0, HARD_LIMIT);
    writeHandles(updated);
    return updated;
  }

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.currentHandle = this.dataset.productHandle || '';
      this.maxProducts = parseInt(this.dataset.maxProducts, 10) || 12;
      this.columnsMobile = parseInt(this.dataset.columnsMobile, 10) || 1;
      this.columnsDesktop = parseInt(this.dataset.columnsDesktop, 10) || 4;
      this.swipeOnMobile = this.dataset.swipeOnMobile === 'true';
      this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
      this.showSecondaryImage = this.dataset.showSecondaryImage === 'true';
      this.grid = this.querySelector('[data-recently-viewed-grid]');
      this.loadingEl = this.querySelector('[data-recently-viewed-loading]');
      this.sliderComponent = this.querySelector('slider-component');
      this.sliderButtons = this.querySelector('[data-recently-viewed-slider-buttons]');

      this.init();
    }

    async init() {
      trackCurrentProduct(this.currentHandle);

      const handles = readHandles()
        .filter((handle) => handle !== this.currentHandle)
        .slice(0, this.maxProducts);

      if (handles.length === 0) {
        this.finish(false);
        return;
      }

      const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle)));

      const validCards = cards.filter(Boolean);

      if (validCards.length === 0) {
        this.finish(false);
        return;
      }

      this.render(validCards);
      this.finish(true);
      this.setupSlider(validCards.length);

      this.triggerEquateRescan();
    }

    /**
     * Re-run the Equate compare app over the freshly injected cards. Mirrors the
     * rescan used by algolia-custom-hooks.js so the compare icon appears exactly
     * as it does on server-rendered card grids. Runs a few staggered attempts
     * because Equate processes the DOM asynchronously after init.
     */
    triggerEquateRescan() {
      const rescan = () => {
        try {
          if (window.SPRCompare) {
            if (typeof window.SPRCompare.init === 'function') window.SPRCompare.init();
            if (typeof window.SPRCompare.render === 'function') window.SPRCompare.render();
            if (typeof window.SPRCompare.refresh === 'function') window.SPRCompare.refresh();
          }
          if (window.EquateCompare) {
            if (typeof window.EquateCompare.init === 'function') window.EquateCompare.init();
            if (typeof window.EquateCompare.render === 'function') window.EquateCompare.render();
          }
          if (window.SPE && typeof window.SPE.init === 'function') window.SPE.init();
          if (typeof window.compareGridRender === 'function') window.compareGridRender();
          if (typeof window.equateAppGetComparedData === 'function') window.equateAppGetComparedData();
        } catch (error) {
        }

        ['equate:init', 'equate:refresh', 'compare:init', 'compare:refresh', 'page:updated'].forEach((evt) => {
          window.dispatchEvent(new CustomEvent(evt));
          document.dispatchEvent(new CustomEvent(evt));
        });
      };

      rescan();
      setTimeout(rescan, 250);
      setTimeout(rescan, 900);
    }

    /**
     * Fetch one product card fragment via the Section Rendering API.
     * @param {string} handle
     * @returns {Promise<string|null>} card HTML, or null if unavailable
     */
    async fetchCard(handle) {
      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = `${rootUrl}products/${encodeURIComponent(handle)}?section_id=recently-viewed-card`;

      try {
        const response = await fetch(url);
        if (!response.ok) return null;

        const html = (await response.text()).trim();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const item = parsed.querySelector('.grid__item');
        if (!item) return null;

        if (!this.showSecondaryImage) {
          const hoverMedia = item.querySelector('.media--hover-effect');
          const images = hoverMedia ? hoverMedia.querySelectorAll('img') : [];
          if (images.length > 1) images[images.length - 1].remove();
        }

        return item.outerHTML;
      } catch (error) {
        return null;
      }
    }

    /**
     * Inject all card fragments into the grid in a single reflow, assigning the
     * `Slide-{sectionId}-{n}` ids the slider script expects.
     * @param {string[]} cardHtmlList
     */
    render(cardHtmlList) {
      const template = document.createElement('template');
      template.innerHTML = cardHtmlList.join('');

      const items = template.content.querySelectorAll('.grid__item');
      items.forEach((item, index) => {
        item.id = `Slide-${this.sectionId}-${index + 1}`;
      });

      this.grid.replaceChildren(template.content);
    }

    /**
     * Decide whether a slider is needed for the actual number of cards, add the
     * matching classes/buttons, and hand off to kingsley-featured-collection.js.
     * @param {number} count number of rendered cards
     */
    setupSlider(count) {
      const showMobileSlider = this.swipeOnMobile && count > this.columnsMobile;
      const showDesktopSlider = this.enableDesktopSlider && count > this.columnsDesktop;

      if (showMobileSlider || showDesktopSlider) {
        this.grid.classList.add('slider');
        if (showDesktopSlider) this.grid.classList.add('slider--desktop');
        if (showMobileSlider) this.grid.classList.add('slider--tablet', 'grid--peek');
        if (this.sliderButtons) this.sliderButtons.hidden = false;
        if (this.sliderComponent && typeof this.sliderComponent.resetPages === 'function') {
          this.sliderComponent.resetPages();
        }

        this.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      } else {
        if (this.sliderButtons) this.sliderButtons.remove();
      }
    }

    /**
     * Remove the loading state and either reveal the section or leave it hidden.
     * @param {boolean} hasProducts
     */
    finish(hasProducts) {
      if (this.loadingEl) this.loadingEl.remove();

      if (hasProducts) {
        this.hidden = false;
      } else {
        this.remove();
      }
    }
  }

  if (!customElements.get('recently-viewed-products')) {
    customElements.define('recently-viewed-products', RecentlyViewedProducts);
  }
})();
